import ProfileHttps from "../../Https/ProfileHttps";
import IdMappingService from "../../IdMapping/IdMappingService";
import ProfileFacade from "./Facade/ProfileFacade";
import OwnProfileBuilder from "./Builders/OwnProfileBuilder";
import ProfileBuilder from "./Builders/ProfileBuilder";
import SDKConfigurationModel from "../../Configurator/Models/SDKConfiguraitonModel";
import FootballFacade from "../Football/Facades/FootballFacade";
import FootballHttps from "../../Https/FootballHttps";
import ActivityHttps from "../../Https/ActivityHttps";
import SDKIdsRemapper from "../../Remapper/SDKIdsRemapper";
import LocalCacheInstanceFactory from "../../Global/Factories/LocalInstanceFactory";
import { LocalCacheInterface } from "../../Global/Interfaces/GlobalInterfaces";
import LeadRequestBody from "./Models/Lead/LeadRequestBody";
import ProfileStatsModel from "./Models/Stats/ProfileStatsModel";
import ConsentRequestBody from "./Models/Consent/ConsentRequestBody";
import ConsentModel from "./Models/Consent/ConsentModel";

export default class Profile {
    private idsRemapper: SDKIdsRemapper = null;
    private profileHttps: ProfileHttps = null;
    private activityHttps: ActivityHttps = null;
    private footballHttps: FootballHttps = null;
    private footballFacade: FootballFacade = null;
    private idMapping: IdMappingService = null;
    private localCache: LocalCacheInterface = null;
    private profileFacade: ProfileFacade = null;
    private ownProfileBuilder: OwnProfileBuilder = null;
    private profileBuilder: ProfileBuilder = null;

    constructor(config: SDKConfigurationModel) {
        this.profileHttps = new ProfileHttps(config);
        this.activityHttps = new ActivityHttps(config);
        this.footballHttps = new FootballHttps(config);
        this.idMapping = new IdMappingService(config);
        this.localCache = new LocalCacheInstanceFactory().createLocalCacheInstance(config.idSchema);
        this.footballFacade = new FootballFacade(config, this.localCache, this.footballHttps, this.idMapping);
        this.profileFacade = new ProfileFacade(this.localCache, this.profileHttps, this.activityHttps, config.errorHandlingMode);
        this.idsRemapper = new SDKIdsRemapper(config, this.footballFacade);
        this.ownProfileBuilder = new OwnProfileBuilder(config, this.idsRemapper, this.localCache, this.profileHttps, this.activityHttps);
        this.profileBuilder = new ProfileBuilder(this.idsRemapper, this.localCache, this.profileHttps, this.activityHttps);
    }

    public getOwn = () => {
        return this.ownProfileBuilder;
    };

    public getById = (id:string) => {
        return this.profileBuilder.getById(id);
    };

    public getCountries = async (disableCache?: boolean) => {
        return await this.profileFacade.getCountries(disableCache);
    };

    public getByIds = async (profileIds?: string[], search?: string, disableCache?: boolean) => {
        return await this.profileFacade.getByIds(profileIds, search, disableCache);
    };

    public collectLead = async (requestBody: LeadRequestBody) => {
        return await this.profileFacade.collectLead(requestBody);
    };

    public checkFollowStatus = async (profileIds: string[]) => {
        return await this.profileFacade.checkFollowStatus(profileIds);
    };

    public getOwnStats = async (): Promise<ProfileStatsModel> => {
        return await this.ownProfileBuilder.getStats();
    };

    public getStats = async (profileId: string, disableCache?: boolean): Promise<ProfileStatsModel> => {
        return await this.profileBuilder.getById(profileId).getStats(disableCache);
    };

    public getOwnBadges = async (): Promise<string[]> => {
        return await this.ownProfileBuilder.getBadges();
    };

    public getBadges = async (profileId: string): Promise<string[]> => {
        return await this.profileBuilder.getById(profileId).getBadges();
    };

    public collectConsents = async (consents: ConsentRequestBody[]) => {
        return await this.profileFacade.collectConsents(consents);
    };

    public getOwnConsents = async (): Promise<ConsentModel[]> => {
        return await this.ownProfileBuilder.getConsents();
    };

    public deleteConsent = async (consentId: string): Promise<boolean> => {
        return await this.ownProfileBuilder.deleteConsent(consentId);
    };
}